import { useState, useEffect } from 'react';
import { apiFetch } from './shared';

const API = '/api';

const FELDER = [
  { key:'gewicht_kg',      label:'Gewicht',      unit:'kg',  step:0.1,  required:true,  color:'#2a6fa8' },
  { key:'koerperfett_pct', label:'Körperfett',   unit:'%',   step:0.1,                  color:'#c17f24' },
  { key:'muskelmasse_kg',  label:'Muskelmasse',  unit:'kg',  step:0.1,                  color:'#4a9c6f' },
  { key:'fettmasse_kg',    label:'Fettmasse',    unit:'kg',  step:0.1,                  color:'#cf7f50' },
  { key:'fettfreie_masse_kg', label:'Fettfrei',  unit:'kg',  step:0.1,                  color:'#7a9c6f' },
  { key:'wasser_pct',      label:'Körperwasser', unit:'%',   step:0.1,                  color:'#5a8fd4' },
  { key:'knochenmasse_kg', label:'Knochenmasse', unit:'kg',  step:0.01,                 color:'#888'    },
  { key:'viszeralfett',    label:'Viszeralfett', unit:'',    step:1,                    color:'#9a5a4a' },
  { key:'bmi',             label:'BMI',          unit:'',    step:0.1,                  color:'#666'    },
];

// ─── SVG Linienchart ──────────────────────────────────────────────────────────
function LineChart({ data, color, unit }) {
  const [tooltip, setTooltip] = useState(null);
  if (!data || data.length < 2) return null;

  const W = 340, H = 90;
  const PAD = { top: 14, right: 14, bottom: 20, left: 34 };
  const iW = W - PAD.left - PAD.right;
  const iH = H - PAD.top  - PAD.bottom;

  const vals   = data.map(d => d.v);
  const rawMin = Math.min(...vals);
  const rawMax = Math.max(...vals);
  const pad    = (rawMax - rawMin) * 0.15 || 0.5;
  const min    = rawMin - pad;
  const max    = rawMax + pad;
  const range  = max - min;

  const xS = i  => PAD.left + (data.length > 1 ? (i / (data.length - 1)) * iW : iW / 2);
  const yS = v  => PAD.top  + (1 - (v - min) / range) * iH;

  const pts  = data.map((d, i) => [xS(i), yS(d.v)]);
  const line = pts.map((p, i) => `${i === 0 ? 'M' : 'L'}${p[0].toFixed(1)},${p[1].toFixed(1)}`).join(' ');
  const area = `${line} L${pts[pts.length-1][0].toFixed(1)},${(H-PAD.bottom).toFixed(1)} L${pts[0][0].toFixed(1)},${(H-PAD.bottom).toFixed(1)} Z`;

  // Y-Achse: 3 Werte
  const yTicks = [rawMin, (rawMin + rawMax) / 2, rawMax];

  // X-Achse: bis 6 Labels gleichmäßig verteilt
  const maxLabels = Math.min(data.length, 5);
  const xLabelIdx = data.length <= maxLabels
    ? data.map((_, i) => i)
    : Array.from({ length: maxLabels }, (_, i) => Math.round(i * (data.length - 1) / (maxLabels - 1)));

  function fmtDatum(d) {
    const [, m, day] = d.split('-');
    return `${parseInt(day)}.${parseInt(m)}.`;
  }

  function fmtVal(v) {
    return unit === 'kg' ? v.toFixed(1) : unit === '%' ? v.toFixed(1) : String(v);
  }

  return (
    <div style={{ position: 'relative' }}>
      <svg
        viewBox={`0 0 ${W} ${H}`}
        style={{ width: '100%', display: 'block', overflow: 'visible' }}
        onMouseLeave={() => setTooltip(null)}
        onTouchEnd={() => setTimeout(() => setTooltip(null), 2000)}
      >
        {/* Horizontale Gridlines + Y-Labels */}
        {yTicks.map((t, i) => (
          <g key={i}>
            <line x1={PAD.left} y1={yS(t).toFixed(1)} x2={W - PAD.right} y2={yS(t).toFixed(1)}
              stroke="#1e1e1e" strokeWidth="1" />
            <text x={PAD.left - 4} y={yS(t) + 3.5} fontSize="7.5" fill="#3a3a3a" textAnchor="end">
              {fmtVal(t)}
            </text>
          </g>
        ))}

        {/* Fläche */}
        <path d={area} fill={color} opacity="0.12" />

        {/* Linie */}
        <path d={line} fill="none" stroke={color} strokeWidth="1.8"
          strokeLinecap="round" strokeLinejoin="round" />

        {/* X-Achse Datum-Labels */}
        {xLabelIdx.map(i => (
          <text key={i} x={xS(i).toFixed(1)} y={H - 4} fontSize="7.5" fill="#3a3a3a" textAnchor="middle">
            {fmtDatum(data[i].datum)}
          </text>
        ))}

        {/* Datenpunkte */}
        {pts.map((p, i) => {
          const isLast = i === data.length - 1;
          return (
            <g key={i}
              onMouseEnter={() => setTooltip(i)}
              onTouchStart={() => setTooltip(i)}
              style={{ cursor: 'pointer' }}
            >
              {/* Größerer unsichtbarer Touch-Bereich */}
              <circle cx={p[0]} cy={p[1]} r="10" fill="transparent" />
              <circle cx={p[0]} cy={p[1]} r={isLast ? 4 : 3}
                fill={isLast ? color : '#181818'}
                stroke={color} strokeWidth={isLast ? 0 : 1.5} />
              {/* Letzter Wert immer anzeigen */}
              {isLast && (
                <text x={p[0]} y={p[1] - 7} fontSize="9" fill={color}
                  textAnchor="middle" fontWeight="bold">
                  {fmtVal(data[i].v)}{unit}
                </text>
              )}
            </g>
          );
        })}

        {/* Tooltip bei Hover/Touch */}
        {tooltip !== null && tooltip !== data.length - 1 && (() => {
          const p = pts[tooltip];
          const d = data[tooltip];
          const tx = Math.min(Math.max(p[0], 36), W - 36);
          const ty = p[1] < 30 ? p[1] + 18 : p[1] - 20;
          return (
            <g>
              <rect x={tx - 28} y={ty - 11} width={56} height={16}
                fill="#1a1a1a" stroke={color} strokeWidth="0.8" rx="3" />
              <text x={tx} y={ty} fontSize="8.5" fill={color} textAnchor="middle" fontWeight="bold">
                {fmtVal(d.v)}{unit}
              </text>
              <text x={tx} y={ty + 8} fontSize="7" fill="#555" textAnchor="middle">
                {fmtDatum(d.datum)}
              </text>
            </g>
          );
        })()}
      </svg>
    </div>
  );
}

// ─── Hauptkomponente ──────────────────────────────────────────────────────────
export default function Koerper() {
  const today = new Date().toISOString().slice(0, 10);
  const [form, setForm]         = useState({ datum: today });
  const [status, setStatus]     = useState('idle');
  const [verlauf, setVerlauf]   = useState([]);
  const [withings, setWithings] = useState(null);
  const [syncStatus, setSyncStatus] = useState('idle');
  const [showForm, setShowForm] = useState(false);

  useEffect(() => {
    apiFetch(API + '/koerper')
      .then(r => r.json())
      .then(rows => {
        setVerlauf(rows);
        if (rows.length > 0) {
          const last = rows[0];
          const prefill = { datum: today };
          FELDER.forEach(f => { if (last[f.key] != null) prefill[f.key] = last[f.key]; });
          setForm(prefill);
        }
      }).catch(() => {});
    apiFetch(API + '/koerper/withings-status')
      .then(r => r.json()).then(setWithings).catch(() => {});
  }, [today]);

  function withingsSync() {
    setSyncStatus('syncing');
    apiFetch(API + '/koerper/withings-sync')
      .then(r => r.json())
      .then(d => {
        setSyncStatus(d.success ? 'done:' + (d.imported || 0) : 'error');
        if (d.success) {
          apiFetch(API + '/koerper').then(r => r.json()).then(rows => {
            setVerlauf(rows);
            if (rows.length > 0) {
              const last = rows[0];
              const prefill = { datum: today };
              FELDER.forEach(f => { if (last[f.key] != null) prefill[f.key] = last[f.key]; });
              setForm(prefill);
            }
          }).catch(() => {});
        }
        setTimeout(() => setSyncStatus('idle'), 5000);
      })
      .catch(() => { setSyncStatus('error'); setTimeout(() => setSyncStatus('idle'), 3000); });
  }

  function save() {
    setStatus('saving');
    apiFetch(API + '/koerper', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(form),
    }).then(r => r.json()).then(() => {
      setStatus('saved');
      apiFetch(API + '/koerper').then(r => r.json()).then(setVerlauf).catch(() => {});
      setTimeout(() => { setStatus('idle'); setShowForm(false); }, 2000);
    }).catch(() => { setStatus('error'); setTimeout(() => setStatus('idle'), 2000); });
  }

  const last = verlauf[0];
  const prev = verlauf[1];
  // Ältestes zuerst für Charts
  const chronological = verlauf.slice().reverse();

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>

      {/* ─── Withings ─── */}
      <div style={{ background: '#141414', borderRadius: 8, overflow: 'hidden' }}>
        <div style={{ padding: '10px 14px', background: '#1c1c1c', borderBottom: '1px solid #222', fontSize: 12, color: '#888', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <span>Withings</span>
          {withings && (
            <span style={{ fontSize: 11, color: withings.connected ? '#4a9c6f' : '#555' }}>
              {withings.connected ? '● verbunden' : '○ nicht verbunden'}
            </span>
          )}
        </div>
        <div style={{ padding: '10px 14px', display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center' }}>
          {withings?.connected ? (
            <>
              <button onClick={withingsSync} disabled={syncStatus === 'syncing'} style={{
                background: syncStatus.startsWith('done') ? '#1a3a2a' : syncStatus === 'error' ? '#3a1a1a' : '#141414',
                border: '1px solid ' + (syncStatus.startsWith('done') ? '#2a5c3a' : syncStatus === 'error' ? '#5c2a2a' : '#2a5c2a'),
                borderRadius: 6, padding: '7px 14px',
                color: syncStatus.startsWith('done') ? '#5abf7a' : syncStatus === 'error' ? '#cf7f7f' : '#4a9c6f',
                fontSize: 12, cursor: 'pointer', fontFamily: 'inherit',
              }}>
                {syncStatus === 'syncing' ? 'Synchronisiere...'
                  : syncStatus.startsWith('done') ? `✓ ${syncStatus.split(':')[1]} neu`
                  : syncStatus === 'error' ? '✗ Fehler'
                  : '↓ Sync'}
              </button>
              <span style={{ fontSize: 10, color: '#333' }}>tägl. 08:00</span>
            </>
          ) : (
            <a href="/api/koerper/withings-auth" style={{ background: '#0d1020', border: '1px solid #2a3a5c', borderRadius: 6, padding: '7px 14px', color: '#5a8fd4', fontSize: 12, textDecoration: 'none' }}>
              Withings verbinden →
            </a>
          )}
        </div>
      </div>

      {/* ─── Aktuelle Werte ─── */}
      {last && (
        <div style={{ background: '#141414', borderRadius: 8, padding: '12px 14px' }}>
          <div style={{ fontSize: 10, color: '#444', marginBottom: 10, display: 'flex', justifyContent: 'space-between' }}>
            <span style={{ letterSpacing: 1 }}>AKTUELL</span>
            <span>{new Date(last.datum + 'T12:00:00').toLocaleDateString('de-DE', { day: 'numeric', month: 'short', year: 'numeric' })}</span>
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(100px, 1fr))', gap: 8 }}>
            {FELDER.filter(f => last[f.key] != null).map(f => {
              const delta = prev?.[f.key] != null ? Math.round((last[f.key] - prev[f.key]) * 100) / 100 : null;
              const goesDown = ['gewicht_kg','koerperfett_pct','fettmasse_kg','viszeralfett','bmi'].includes(f.key);
              const isGood = delta !== null && (goesDown ? delta < 0 : delta > 0);
              const isBad  = delta !== null && (goesDown ? delta > 0 : delta < 0);
              return (
                <div key={f.key} style={{ background: '#1a1a1a', borderRadius: 6, padding: '8px 10px', borderLeft: `2px solid ${f.color}` }}>
                  <div style={{ fontSize: 9, color: '#444', marginBottom: 3 }}>{f.label}</div>
                  <div style={{ fontSize: 16, fontWeight: 700, color: '#e0e0e0' }}>
                    {last[f.key]}<span style={{ fontSize: 10, color: '#555', marginLeft: 2 }}>{f.unit}</span>
                  </div>
                  {delta !== null && delta !== 0 && (
                    <div style={{ fontSize: 10, color: isGood ? '#4a9c6f' : isBad ? '#c17f24' : '#555', marginTop: 2 }}>
                      {delta > 0 ? '+' : ''}{delta}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* ─── Charts ─── */}
      {chronological.length >= 2 && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 1, background: '#141414', borderRadius: 8, overflow: 'hidden' }}>
          <div style={{ padding: '10px 14px', background: '#1c1c1c', borderBottom: '1px solid #222', fontSize: 12, color: '#888' }}>
            Verlauf · {chronological.length} Messungen
          </div>
          {FELDER.filter(f => chronological.filter(r => r[f.key] != null).length >= 2).map(f => {
            const data = chronological
              .filter(r => r[f.key] != null)
              .map(r => ({ datum: r.datum, v: parseFloat(r[f.key]) }));
            return (
              <div key={f.key} style={{ padding: '12px 14px', borderBottom: '1px solid #1a1a1a' }}>
                <div style={{ fontSize: 11, color: f.color, marginBottom: 6, fontWeight: 600 }}>
                  {f.label} <span style={{ fontWeight: 400, color: '#444' }}>{f.unit}</span>
                </div>
                <LineChart data={data} color={f.color} unit={f.unit} />
              </div>
            );
          })}
        </div>
      )}

      {/* ─── Neue Messung ─── */}
      <div style={{ background: '#141414', borderRadius: 8, overflow: 'hidden' }}>
        <button
          onClick={() => setShowForm(f => !f)}
          style={{ width: '100%', background: 'transparent', border: 'none', padding: '12px 14px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', cursor: 'pointer', fontFamily: 'inherit' }}
        >
          <span style={{ fontSize: 12, color: '#888' }}>Manuelle Messung eingeben</span>
          <span style={{ fontSize: 12, color: '#444' }}>{showForm ? '▲' : '▼'}</span>
        </button>

        {showForm && (
          <div style={{ padding: '0 14px 14px', display: 'flex', flexDirection: 'column', gap: 10 }}>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(140px,1fr))', gap: 8 }}>
              <div>
                <div style={{ fontSize: 10, color: '#555', marginBottom: 3 }}>Datum</div>
                <input type="date" value={form.datum || today}
                  onChange={e => setForm(f => ({ ...f, datum: e.target.value }))}
                  style={{ width: '100%', background: '#1a1a1a', border: '1px solid #333', borderRadius: 6, padding: '7px 10px', color: '#e0e0e0', fontSize: 13, fontFamily: 'inherit', boxSizing: 'border-box' }} />
              </div>
              {FELDER.map(f => (
                <div key={f.key}>
                  <div style={{ fontSize: 10, color: '#555', marginBottom: 3 }}>
                    {f.label}{f.required ? ' *' : ''} <span style={{ color: '#333' }}>{f.unit}</span>
                  </div>
                  <input type="number" step={f.step} value={form[f.key] ?? ''}
                    onChange={e => setForm(fr => ({ ...fr, [f.key]: e.target.value === '' ? undefined : parseFloat(e.target.value) }))}
                    placeholder="—"
                    style={{ width: '100%', background: '#1a1a1a', border: '1px solid #333', borderRadius: 6, padding: '7px 10px', color: '#e0e0e0', fontSize: 13, fontFamily: 'inherit', outline: 'none', boxSizing: 'border-box' }}
                  />
                </div>
              ))}
            </div>
            <input type="text" placeholder="Notiz (optional)" value={form.notiz || ''}
              onChange={e => setForm(f => ({ ...f, notiz: e.target.value }))}
              style={{ background: '#1a1a1a', border: '1px solid #333', borderRadius: 6, padding: '8px 10px', color: '#e0e0e0', fontSize: 13, fontFamily: 'inherit', outline: 'none' }} />
            <button onClick={save} disabled={!form.gewicht_kg || status === 'saving'} style={{
              background: status === 'saved' ? '#1a3a2a' : status === 'error' ? '#3a1a1a' : '#1a2a3a',
              color: '#fff', border: 'none', borderRadius: 6, padding: '11px',
              fontSize: 14, fontWeight: 700, cursor: 'pointer', fontFamily: 'inherit',
            }}>
              {status === 'saved' ? 'Gespeichert ✓' : status === 'error' ? 'Fehler' : status === 'saving' ? '...' : 'Speichern'}
            </button>
          </div>
        )}
      </div>

    </div>
  );
}
